import type { RicosDocumentNode } from '@wix/ricos/schema-next';
import { get, set } from 'lodash';

type Attributes = { [attribute in string]?: string };

type NodeFieldType = 'boolean' | 'string' | 'color' | 'int' | 'enum' | 'float';

type AttributeSerdes = {
  ser: (target: Attributes, source: RicosDocumentNode) => void;
  des: (target: RicosDocumentNode, source: Attributes) => void;
};

export const imageAttrs: AttributeSerdes = compose([
  imageSource('src', ['imageData', 'image', 'src', 'id'], ['imageData', 'image', 'src', 'url']),
  mappedAttribute('int', 'originalwidth', ['imageData', 'image', 'width']),
  mappedAttribute('int', 'originalheight', ['imageData', 'image', 'height']),
  mappedAttribute('int', 'customWidth', ['imageData', 'containerData', 'width', 'custom']),
  mappedAttribute('enum', 'width', ['imageData', 'containerData', 'width', 'size']),
  mappedAttribute('enum', 'alignment', ['imageData', 'containerData', 'alignment']),
  mappedAttribute('string', 'alt', ['imageData', 'altText']),
]);

export const htmlAttrs: AttributeSerdes = compose([
  mappedAttribute('enum', 'alignment', ['htmlData', 'containerData', 'alignment']),
  mappedAttribute('string', 'width', ['htmlData', 'containerData', 'width', 'custom']),
  mappedAttribute('string', 'height', ['htmlData', 'containerData', 'height', 'custom']),
]);

export const tableAttrs: AttributeSerdes = compose([
  mappedAttribute('boolean', 'rowheader', ['tableData', 'rowHeader']),
  mappedAttribute('boolean', 'columnheader', ['tableData', 'columnHeader']),
]);

export const tableCellAttrs: AttributeSerdes = compose([
  mappedAttribute('enum', 'verticalalignment', ['tableCellData', 'cellStyle', 'verticalAlignment']),
  colorClassAttribute(['tableCellData', 'cellStyle', 'backgroundColor'], 'bg-'),
  ...['left', 'right', 'top', 'bottom'].map(pos =>
    colorClassAttribute(['tableCellData', 'borderColors', pos], `border-${pos}-`)
  ),
]);

export const collapsibleListAttrs: AttributeSerdes = compose([
  mappedAttribute('string', 'initialexpandeditems', [
    'collapsibleListData',
    'initialExpandedItems',
  ]),
  mappedAttribute('boolean', 'expandonlyone', ['collapsibleListData', 'expandOnlyOne']),
]);

export const buttonAttrs: AttributeSerdes = compose([
  mappedAttribute('string', 'text', ['buttonData', 'text']),
  mappedAttribute('string', 'href', ['buttonData', 'link', 'url']),
  mappedAttribute('enum', 'alignment', ['buttonData', 'containerData', 'alignment']),
  colorClassAttribute(['buttonData', 'styles', 'colors', 'background'], 'bg-'),
  colorClassAttribute(['buttonData', 'styles', 'colors', 'border'], 'border-'),
  colorClassAttribute(['buttonData', 'styles', 'colors', 'text'], 'text-'),
  intClassAttribute(['buttonData', 'styles', 'border', 'width'], 'border-'),
  intClassAttribute(['buttonData', 'styles', 'border', 'radius'], 'rounded-'),
]);

export const fileAttrs: AttributeSerdes = compose([
  mappedAttribute('string', 'type', ['fileData', 'type']),
  mappedAttribute('string', 'name', ['fileData', 'name']),
  mappedAttribute('enum', 'size', ['fileData', 'containerData', 'width', 'size']),
  mappedAttribute('enum', 'alignment', ['fileData', 'containerData', 'alignment']),
  mappedAttribute('boolean', 'textWrap', ['fileData', 'containerData', 'textWrap']),
  mappedAttribute('string', 'url', ['fileData', 'src', 'url']),
  mappedAttribute('boolean', 'private', ['fileData', 'src', 'private']),
  mappedAttribute('int', 'fileSize', ['fileData', 'size']),
  mappedAttribute('string', 'fileSizeInKb', ['fileData', 'sizeInKb']),
]);

export const gifAttrs: AttributeSerdes = compose([
  mappedAttribute('enum', 'gifType', ['gifData', 'gifType']),
  mappedAttribute('string', 'originalGif', ['gifData', 'original', 'gif']),
  mappedAttribute('string', 'originalMp4', ['gifData', 'original', 'mp4']),
  mappedAttribute('string', 'originalThumbnail', ['gifData', 'original', 'still']),
  mappedAttribute('string', 'downsizedGif', ['gifData', 'downsized', 'gif']),
  mappedAttribute('string', 'downsizedMp4', ['gifData', 'downsized', 'mp4']),
  mappedAttribute('string', 'downsizedThumbnail', ['gifData', 'downsized', 'still']),
  mappedAttribute('string', 'originalWidth', ['gifData', 'width']),
  mappedAttribute('string', 'originalHeight', ['gifData', 'height']),
  mappedAttribute('enum', 'alignment', ['gifData', 'containerData', 'alignment']),
  mappedAttribute('int', 'customWidth', ['gifData', 'containerData', 'width', 'custom']),
  mappedAttribute('enum', 'width', ['gifData', 'containerData', 'width', 'size']),
]);

export const audioAttrs: AttributeSerdes = compose([
  mappedAttribute('string', 'url', ['audioData', 'audio', 'src', 'url']),
  mappedAttribute('float', 'duration', ['audioData', 'audio', 'duration']),
  mappedAttribute('string', 'name', ['audioData', 'name']),
  mappedAttribute('string', 'authorName', ['audioData', 'authorName']),
  mappedAttribute('boolean', 'disableDownload', ['audioData', 'disableDownload']),
  mappedAttribute('boolean', 'textWrap', ['audioData', 'containerData', 'textWrap']),
  mappedAttribute('enum', 'alignment', ['audioData', 'containerData', 'alignment']),
  mappedAttribute('int', 'customWidth', ['audioData', 'containerData', 'width', 'custom']),
  mappedAttribute('enum', 'width', ['audioData', 'containerData', 'width', 'size']),
]);

function compose(defs: AttributeSerdes[]): AttributeSerdes {
  return {
    ser: (target, source) => defs.forEach(def => def.ser(target, source)),
    des: (target, source) => defs.forEach(def => def.des(target, source)),
  };
}

function imageSource(rcmlAttribute: string, idPath: string[], urlPath: string[]): AttributeSerdes {
  const urlPattern = /media\/(.*)/;
  return {
    ser: (target, source) => {
      const id = get(source, idPath);
      const url = get(source, urlPath);
      let finalUrl = '';
      if (id) {
        finalUrl = `https://static.wixstatic.com/media/${id}`;
      } else if (url) {
        finalUrl = url;
      }
      target[rcmlAttribute] = finalUrl;
    },
    des: (target, source) => {
      const url = source[rcmlAttribute];
      const matches = typeof url === 'string' ? urlPattern.exec(url) : null;
      if (matches) {
        set(target, idPath, matches[1]);
      } else {
        set(target, urlPath, url);
      }
    },
  };
}

function serializeAttribute(
  fieldType: NodeFieldType,
  attrValue: any,
  rcmlAttribute: string,
  target: Attributes
) {
  if (fieldType === 'string' && attrValue) set(target, [rcmlAttribute], attrValue);
  if (fieldType === 'boolean' && typeof attrValue === 'boolean')
    set(target, [rcmlAttribute], attrValue ? 'true' : 'false');
  if (fieldType === 'int' && !isNaN(parseInt(attrValue)))
    set(target, [rcmlAttribute], parseInt(attrValue));
  if (fieldType === 'enum' && typeof attrValue === 'string')
    set(target, [rcmlAttribute], attrValue.toLowerCase());
  if (fieldType === 'float' && !isNaN(parseFloat(attrValue)))
    set(target, [rcmlAttribute], parseFloat(attrValue));
}

function deserializeAttribute(
  fieldType: NodeFieldType,
  attrValue: any,
  nodePath: string[],
  target: RicosDocumentNode
) {
  if (fieldType === 'string' && attrValue) set(target, nodePath, attrValue);
  if (fieldType === 'boolean' && attrValue) set(target, nodePath, attrValue === 'true');
  if (fieldType === 'int' && attrValue && !isNaN(parseInt(attrValue, 10)))
    set(target, nodePath, parseInt(attrValue, 10));
  if (fieldType === 'enum' && typeof attrValue === 'string')
    set(target, nodePath, attrValue.toUpperCase());
  if (fieldType === 'float' && attrValue && !isNaN(parseFloat(attrValue)))
    set(target, nodePath, parseFloat(attrValue));
}

function mappedAttribute(
  fieldType: NodeFieldType,
  rcmlAttribute: string,
  nodePath: string[]
): AttributeSerdes {
  return {
    ser: (target, source) => {
      const attrValue = get(source, nodePath);
      serializeAttribute(fieldType, attrValue, rcmlAttribute, target);
    },
    des: (target, source) => {
      const attrValue = source[rcmlAttribute];
      deserializeAttribute(fieldType, attrValue, nodePath, target);
    },
  };
}

function colorClassAttribute(nodePath: string[], colorPrefix: string): AttributeSerdes {
  return regexGroupingAttribute(nodePath, colorPrefix, /#[\da-fA-F]{6}/);
}

function intClassAttribute(nodePath: string[], intPrefix: string): AttributeSerdes {
  return regexGroupingAttribute(nodePath, intPrefix, /[\d]+/);
}

function regexGroupingAttribute(
  nodePath: string[],
  valuePrefix: string,
  valuePattern: RegExp
): AttributeSerdes {
  const classValuePattern = new RegExp(valuePrefix + '(' + valuePattern.source + ')');
  return {
    ser: (target, source) => {
      const value = get(source, nodePath);
      if (value && valuePattern.test(value))
        target.class = [target.class, valuePrefix + value].filter(c => c?.length).join(' ');
    },
    des: (target, source) => {
      const classes = (source.class || '').split(' ');
      for (let i = 0; i < classes.length; i++) {
        const classs = classes[i];
        const matches = classValuePattern.exec(classs);
        if (matches?.length) {
          set(target, nodePath, matches[1]);
        }
      }
    },
  };
}
